import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { Session } from "../api/parse.ts";
import { closeDialog, useDialog } from "./admin.tsx";
import { useDialogFocusTrap } from "./dialogFocus.ts";
import { IconAction } from "./iconAction.tsx";
import { TagEditor } from "./chipPicker.tsx";
import { ColumnFilter, FilterChips, type FilterChip, type FilterChoice } from "./columnFilter.tsx";
import { DataTable, type DataColumn } from "./dataTable.tsx";
import type { InventoryLoad } from "./inventory.ts";
import { operatorModeLabel } from "./session.tsx";
import { cleanedReason } from "../sessionReason.ts";
import {
  DEVICES_TABLE_ID,
  SETTINGS_DEVICES_TABLE_ID,
  FILTER_COLUMNS,
  NO_FILTERS,
  PARTIAL_BANNER,
  UNTAGGED_LABEL,
  createFacetMemo,
  filterRows,
  hasFilters,
  optionLabel,
  pruneFilters,
  readTableQuery,
  selectedCount,
  sortRows,
  tagLabel,
  toggleUntagged,
  toggleValue,
  writeTableQuery,
  type FilterColumn,
  type Filters,
  type Sort,
} from "./tableState.ts";
import { deviceDetailFields, renderDeviceDetailValue } from "./deviceDetailFields.tsx";
import {
  DEFAULT_PLACEMENT_SORT,
  NO_PLACEMENT_FILTERS,
  PLACEMENT_COLUMN_LABELS,
  PLACEMENT_FILTER_COLUMNS,
  createPlacementFacetMemo,
  hasPlacementFilters,
  selectedPlacementFilterCount,
  sortPlacementRows,
  togglePlacementFilter,
  type PlacementFilterColumn,
  type PlacementFilters,
  type PlacementSort,
} from "./placementTableState.ts";

export type DeviceRow = {
  deviceId: string;
  name: string;
  hostname: string;
  presence: "Online" | "Offline";
  lastHeartbeatAt: string | null;
  lastHeartbeatLabel: string;
  lastBootAt: string | null;
  lastBootLabel: string;
  loggedOnUser: string;
  status: "Pending" | "Enrolled" | "Revoked" | "Unknown";
  agentVersion: string;
  osLabel: string;
  platform?: string;
  osVersion?: string;
  capabilities?: string[];
  minimumAgentVersion?: string | null;
  protocolVersion?: string | null;
  osArchitecture?: string;
  environment?: string | null;
  createdAt?: string | null;
  createdAtLabel?: string;
  approvedAtLabel?: string;
  revokedAt?: string | null;
  revokedAtLabel?: string;
  revokeReason?: string | null;
  lastSeenIp?: string | null;
  displaySummary?: string;
  organisationId: string | null;
  organisationName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  tags: { key: string; value: string }[];
  recordingSelectable: boolean;
  recordingForced?: boolean;
  stealthCommand?: boolean;
  reasonRequired?: boolean;
};

export type DeviceFolder = {
  organisationId: string;
  name: string;
  departments: { departmentId: string; name: string }[];
};

export type FolderSelection =
  | { kind: "root" }
  | { kind: "pending" }
  | { kind: "organisation"; organisationId: string }
  | { kind: "department"; organisationId: string; departmentId: string };

export const DEVICE_TREE_PANEL_KEY = "wylo-rc.devices.tree-panel";

type Features = {
  clipboard: boolean;
  fileTransfer: boolean;
  recording: boolean;
};
export function allowsCapability(device: DeviceRow, name: string): boolean {
  return device.capabilities === undefined || device.capabilities.includes(name);
}

export function osClaimMismatch(platform: string | undefined, version: string | undefined): boolean {
  if (!platform || !version) return false;
  const text = version.toLowerCase();
  const hint = text.includes("windows")
    ? "windows"
    : text.includes("android")
      ? "android"
      : text.includes("ios") || text.includes("iphone") || text.includes("ipad")
        ? "ios"
        : text.includes("mac")
          ? "macos"
          : null;
  return hint !== null && hint !== platform;
}

function startFeatures(device: DeviceRow, recordChecked: boolean): Features {
  const recording =
    device.recordingForced === true || (device.recordingSelectable && recordChecked);
  return {
    clipboard: allowsCapability(device, "clipboard"),
    fileTransfer: allowsCapability(device, "fileTransfer"),
    recording: recording && allowsCapability(device, "record"),
  };
}

function showsRecordChoice(device: DeviceRow): boolean {
  return allowsCapability(device, "record") && (device.recordingForced === true || device.recordingSelectable);
}

/** Stealth Command recording is decided server-side; the dialog shows policy, not a request. */
export function stealthCommandRecordChecked(device: DeviceRow): boolean {
  return device.recordingForced === true || device.recordingSelectable === true;
}

function treeDepartments(organisation: DeviceFolder): DeviceFolder["departments"] {
  if (organisation.name.trim().toLowerCase() !== "unassigned") return organisation.departments;
  return organisation.departments.filter((department) => department.name.trim().toLowerCase() !== "unassigned");
}

export function flattenTreeDepartments(catalog: DeviceFolder[]): DeviceFolder[] {
  return catalog.map((organisation) => {
    const departments = treeDepartments(organisation);
    return departments.length === organisation.departments.length ? organisation : { ...organisation, departments };
  });
}

function tags(device: DeviceRow): string {
  return device.tags.length === 0 ? "None" : device.tags.map(tagLabel).join(", ");
}

export function isWyloOrg(name: string): boolean {
  return name.trim().toLowerCase() === "wylo";
}

function sameSelection(left: FolderSelection, right: FolderSelection): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === "organisation" && right.kind === "organisation") return left.organisationId === right.organisationId;
  if (left.kind === "department" && right.kind === "department") {
    return left.organisationId === right.organisationId && left.departmentId === right.departmentId;
  }
  return true;
}

export function operatorListedDevices(devices: readonly DeviceRow[]): DeviceRow[] {
  return devices.filter((device) => device.status !== "Pending" && device.status !== "Revoked");
}

function folderRows(devices: DeviceRow[], selection: FolderSelection): DeviceRow[] {
  const listed = operatorListedDevices(devices);
  if (selection.kind === "root") return listed;
  if (selection.kind === "organisation") return listed.filter((device) => device.organisationId === selection.organisationId);
  if (selection.kind === "department") return listed.filter((device) => device.departmentId === selection.departmentId);
  return listed;
}

type FolderCounts = {
  all: number;
  organisations: Map<string, number>;
  departments: Map<string, number>;
};

function folderCounts(rows: readonly DeviceRow[]): FolderCounts {
  const organisations = new Map<string, number>();
  const departments = new Map<string, number>();
  for (const row of rows) {
    if (row.organisationId) organisations.set(row.organisationId, (organisations.get(row.organisationId) ?? 0) + 1);
    if (row.departmentId) departments.set(row.departmentId, (departments.get(row.departmentId) ?? 0) + 1);
  }
  return { all: rows.length, organisations, departments };
}

const COLUMN_LABELS: Readonly<Record<FilterColumn, string>> = {
  name: "Name",
  hostname: "Hostname",
  presence: "Online",
  status: "Status",
  lastSeen: "Last seen",
  os: "OS",
  tags: "Tags",
  lastBoot: "Last boot",
  user: "Logged-on user",
  agent: "Agent version",
};

const ROW_WINDOW = 200;
const TAGS_SHOWN = 4;
const ROOT_BRANCH = "root-tree";

function readTreePanelCollapsed(): boolean {
  try {
    return localStorage.getItem(DEVICE_TREE_PANEL_KEY) === "collapsed";
  } catch {
    return false;
  }
}

function writeTreePanelCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(DEVICE_TREE_PANEL_KEY, collapsed ? "collapsed" : "open");
  } catch {
    /* ignore */
  }
}

function figure(count: number): string {
  return count.toLocaleString("en-GB");
}

function countText(count: number, loaded: number, partial: boolean): string {
  return partial ? `${figure(count)} of ${figure(loaded)} loaded` : figure(count);
}

function clockLines(label: string): { date: string; time: string } {
  const space = label.indexOf(" ");
  if (space < 0 || !/^\d{4}-\d{2}-\d{2}$/.test(label.slice(0, space))) return { date: label, time: "" };
  return { date: label.slice(0, space), time: label.slice(space + 1) };
}

function TwoLine(props: { primary: string; secondary: string }) {
  return (
    <span className="device-two-line">
      <span className="cell-clip">{props.primary}</span>
      {props.secondary.length > 0 ? (
        <>
          {" "}
          <span className="cell-clip device-status">{props.secondary}</span>
        </>
      ) : null}
    </span>
  );
}

function TagList(props: { tags: DeviceRow["tags"] }) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  if (props.tags.length === 0) return <>None</>;
  const labels = props.tags.map(tagLabel);
  const hidden = labels.length - TAGS_SHOWN;
  const shown = open || hidden <= 0 ? labels : labels.slice(0, TAGS_SHOWN);
  return (
    <>
      <ul className="device-tag-list" id={listId}>
        {shown.map((label, index) => (
          <li key={index}>{label}</li>
        ))}
      </ul>
      {hidden > 0 ? (
        <button type="button" className="device-tag-more" aria-expanded={open} aria-controls={listId} onClick={() => setOpen(!open)}>
          {open ? "Show fewer" : `+${hidden} more`}
        </button>
      ) : null}
    </>
  );
}

function DeviceActions(props: {
  device: DeviceRow;
  canStart: boolean;
  showConnect: boolean;
  onDetails: () => void;
  onStart: (accessMode: "Attended" | "Unattended") => void;
  onStealth?: () => void;
}) {
  const stealth = Boolean(props.device.stealthCommand && allowsCapability(props.device, "command") && props.onStealth);
  const remote = operatorModeLabel("Attended");
  const quiet = operatorModeLabel("Unattended");
  return (
    <div className="actions">
      <IconAction label="Details" kind="details" onClick={props.onDetails} />
      {props.showConnect && allowsCapability(props.device, "view") ? (
        <IconAction label={remote} kind="control" disabled={!props.canStart} onClick={() => props.onStart("Attended")} />
      ) : null}
      {props.showConnect && allowsCapability(props.device, "view") && allowsCapability(props.device, "unattended") ? (
        <IconAction label={quiet} kind="stealth" disabled={!props.canStart} onClick={() => props.onStart("Unattended")} />
      ) : null}
      {props.showConnect && stealth ? <IconAction label="Stealth Command" kind="command" onClick={() => props.onStealth?.()} /> : null}
    </div>
  );
}

export function DeviceList(props: {
  devices: DeviceRow[];
  catalog: DeviceFolder[];
  error: string | null;
  load: InventoryLoad;
  now?: () => number;
  allowConnect?: boolean;
  canApprove: boolean;
  onStart: (deviceId: string, accessMode: Session["accessMode"], features: Features, reason: string) => void;
  onStealth?: (deviceId: string, justification: string, reason: string) => void;
}) {
  const allowConnect = props.allowConnect !== false;
  const dialogOpenerRef = useRef<HTMLElement | null>(null);
  function rememberDialogOpener() {
    dialogOpenerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  function restoreDialogOpener() {
    dialogOpenerRef.current?.focus();
    dialogOpenerRef.current = null;
  }
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [recordOn, setRecordOn] = useState<Record<string, boolean>>({});
  const [ask, setAsk] = useState<ReasonAsk | null>(null);
  const [ticket, setTicket] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [selection, setSelection] = useState<FolderSelection>({ kind: "root" });
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [treePanelCollapsed, setTreePanelCollapsed] = useState(() => readTreePanelCollapsed());
  const [query] = useState(() => readTableQuery(window.location.search));
  const [filters, setFilters] = useState<Filters>(query.filters);
  const [sort, setSort] = useState<Sort>(query.sort);
  const [rowWindow, setRowWindow] = useState(ROW_WINDOW);
  const pruned = useRef(false);
  const treeId = useId();
  const facetMemo = useMemo(() => createFacetMemo(), []);
  const clock = useRef(props.now ?? Date.now);
  clock.current = props.now ?? Date.now;
  const asOf = useMemo(() => clock.current(), [props.devices]);
  const listed = useMemo(() => operatorListedDevices(props.devices), [props.devices]);
  const scoped = useMemo(() => folderRows(props.devices, selection), [props.devices, selection]);
  const facets = facetMemo(scoped, filters, asOf);
  const sorted = useMemo(() => sortRows(facets.rows, sort), [facets.rows, sort]);
  const treeRows = useMemo(() => filterRows(listed, filters, asOf), [listed, filters, asOf]);
  const counts = useMemo(() => folderCounts(treeRows), [treeRows]);
  const treeCatalog = useMemo(() => flattenTreeDepartments(props.catalog), [props.catalog]);
  const partial = props.load === "partial";
  const loaded = listed.length;
  const asked = ask ? props.devices.find((item) => item.deviceId === ask.deviceId) ?? null : null;
  const detailed = detailsId ? props.devices.find((item) => item.deviceId === detailsId) ?? null : null;
  const dialogRecordChecked =
    asked && showsRecordChoice(asked)
      ? asked.recordingForced === true || recordOn[asked.deviceId] === true
      : false;
  const treePanelId = `${treeId}-panel`;
  useEffect(() => {
    if (!ask) return;
    if (props.devices.some((device) => device.deviceId === ask.deviceId)) return;
    setAsk(null);
    setTicket("");
    setReasonText("");
  }, [ask, props.devices]);
  useEffect(() => {
    if (pruned.current || props.load !== "complete") return;
    pruned.current = true;
    const next = pruneFilters(filters, listed, asOf, { partial: false });
    if (next !== filters) update(next, sort);
  }, [props.load, listed, asOf]);

  function update(nextFilters: Filters, nextSort: Sort) {
    setFilters(nextFilters);
    setSort(nextSort);
    if (query.frozen) return;
    const search = writeTableQuery(window.location.search, nextFilters, nextSort);
    if (search === window.location.search) return;
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${search}`);
  }

  function toggle(column: FilterColumn, value: string) {
    update(toggleValue(filters, column, value), sort);
  }

  function toggleFlag() {
    update(toggleUntagged(filters), sort);
  }

  function onSort(key: string) {
    const column = FILTER_COLUMNS.find((item) => item === key);
    if (!column) return;
    update(filters, { column, direction: sort.column === column && sort.direction === "asc" ? "desc" : "asc" });
  }

  function choices(column: FilterColumn): FilterChoice[] {
    const facet = facets.columns.get(column);
    const selected = filters.values.get(column);
    const list: FilterChoice[] = (facet?.options ?? []).map((option) => ({
      key: `value:${option.value}`,
      label: option.label,
      count: countText(option.count, loaded, partial),
      checked: selected?.has(option.value) === true,
      onToggle: () => toggle(column, option.value),
    }));
    if (column === "tags" && facet && facet.untagged !== null) {
      list.unshift({
        key: "flag:untagged",
        label: UNTAGGED_LABEL,
        count: countText(facet.untagged, loaded, partial),
        checked: filters.untagged,
        onToggle: toggleFlag,
      });
    }
    return list;
  }

  const chips: FilterChip[] = [];
  for (const column of FILTER_COLUMNS) {
    for (const value of filters.values.get(column) ?? []) {
      chips.push({
        key: `value:${column}:${value}`,
        column: COLUMN_LABELS[column],
        label: optionLabel(column, value),
        onRemove: () => toggle(column, value),
      });
    }
    if (column === "tags" && filters.untagged) {
      chips.push({ key: "flag:untagged", column: COLUMN_LABELS.tags, label: UNTAGGED_LABEL, onRemove: toggleFlag });
    }
  }

  function filterFor(column: FilterColumn) {
    return <ColumnFilter label={COLUMN_LABELS[column]} choices={() => choices(column)} selected={selectedCount(filters, column)} />;
  }

  function textColumn(column: FilterColumn, defaultWidth: number, minWidth: number, value: (device: DeviceRow) => string): DataColumn<DeviceRow> {
    return {
      key: column,
      label: COLUMN_LABELS[column],
      defaultWidth,
      minWidth,
      sortable: true,
      filter: filterFor(column),
      cell: (device) => (
        <span className="cell-clip" title={value(device)}>
          {value(device)}
        </span>
      ),
    };
  }

  const columns: DataColumn<DeviceRow>[] = [
    { ...textColumn("name", 140, 96, (device) => device.name), rowHeader: true },
    textColumn("hostname", 136, 96, (device) => device.hostname),
    textColumn("presence", 104, 72, (device) => device.presence),
    {
      key: "lastSeen",
      label: COLUMN_LABELS.lastSeen,
      defaultWidth: 140,
      minWidth: 96,
      sortable: true,
      className: "device-clock",
      filter: filterFor("lastSeen"),
      cell: (device) => {
        const seen = clockLines(device.lastHeartbeatLabel);
        return <TwoLine primary={seen.date} secondary={seen.time} />;
      },
    },
    {
      key: "tags",
      label: COLUMN_LABELS.tags,
      defaultWidth: 128,
      minWidth: 72,
      sortable: true,
      className: "device-tags",
      filter: filterFor("tags"),
      cell: (device) => <TagList tags={device.tags} />,
    },
    textColumn("user", 108, 96, (device) => device.loggedOnUser),
    {
      key: "actions",
      label: "Actions",
      defaultWidth: 168,
      minWidth: 168,
      className: "device-actions",
      cell: (device) => {
        const canStart = allowConnect && device.status === "Enrolled" && device.presence === "Online";
        return (
          <DeviceActions
            device={device}
            showConnect={allowConnect}
            canStart={canStart}
            onDetails={() => {
              rememberDialogOpener();
              setDetailsId(device.deviceId);
            }}
            onStart={(accessMode) => {
              rememberDialogOpener();
              setTicket("");
              setReasonText("");
              setAsk({ kind: "control", deviceId: device.deviceId, accessMode });
            }}
            onStealth={
              allowConnect && props.onStealth
                ? () => {
                    rememberDialogOpener();
                    setTicket("");
                    setReasonText("");
                    setAsk({ kind: "command", deviceId: device.deviceId });
                  }
                : undefined
            }
          />
        );
      },
    },
  ];

  const selectedOrg = selection.kind === "organisation" || selection.kind === "department" ? selection.organisationId : null;
  const selectedDept = selection.kind === "department" ? selection.departmentId : null;
  const rootOpen = !collapsed.has(ROOT_BRANCH);

  function choose(next: FolderSelection) {
    setSelection(next);
    if (next.kind !== "organisation" && next.kind !== "department") return;
    setCollapsed((current) => {
      const opened = new Set(current);
      opened.delete(ROOT_BRANCH);
      if (next.kind === "department") opened.delete(`org:${next.organisationId}`);
      return opened.size === current.size ? current : opened;
    });
  }

  function toggleTreePanel() {
    setTreePanelCollapsed((current) => {
      const next = !current;
      writeTreePanelCollapsed(next);
      return next;
    });
  }

  function flip(branch: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(branch)) next.delete(branch);
      else next.add(branch);
      return next;
    });
  }

  let countSlot = 0;
  function treeRow(
    itemKey: string,
    label: string,
    next: FolderSelection,
    count: number,
    branch: { key: string; controls: string } | null,
    indent: boolean,
  ) {
    const countId = `${treeId}-count-${countSlot++}`;
    return (
      <div key={itemKey} className="device-tree-row">
        {branch ? (
          <button
            type="button"
            className="device-tree-toggle"
            aria-label={`${label} folders`}
            aria-expanded={!collapsed.has(branch.key)}
            aria-controls={branch.controls}
            onClick={() => flip(branch.key)}
          >
            <span className="device-tree-chevron" aria-hidden="true" />
          </button>
        ) : (
          <span className="device-tree-spacer" aria-hidden="true" />
        )}
        <button
          type="button"
          className={indent ? "device-tree-node device-tree-dept" : "device-tree-node"}
          aria-current={sameSelection(selection, next) ? "true" : undefined}
          aria-describedby={countId}
          onClick={() => choose(next)}
        >
          {label}
        </button>
        <span id={countId} className="device-tree-count">
          {countText(count, loaded, partial)}
        </span>
      </div>
    );
  }

  function departmentRow(organisationId: string, department: { departmentId: string; name: string }) {
    return treeRow(
      department.departmentId,
      department.name,
      { kind: "department", organisationId, departmentId: department.departmentId },
      counts.departments.get(department.departmentId) ?? 0,
      null,
      true,
    );
  }

  const emptyText =
    facets.rows.length > 0 || props.load === "loading" || !hasFilters(filters)
      ? null
      : partial
        ? "None of the loaded devices match these filters."
        : "No devices match these filters.";

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Devices</h2>
      </header>
      {props.error ? <p className="banner error">{props.error}</p> : null}
      {partial ? <p className="banner device-partial">{PARTIAL_BANNER}</p> : null}
      <FilterChips chips={chips} onClearAll={() => update(NO_FILTERS, sort)} />
      {detailed ? (
        <DeviceDetailsDialog
          device={detailed}
          onClose={() => {
            setDetailsId(null);
            restoreDialogOpener();
          }}
        />
      ) : null}
      {ask && asked ? (
        <ReasonDialog
          key={ask.kind === "control" ? `${ask.deviceId}:${ask.accessMode}` : `${ask.deviceId}:command`}
          ask={ask}
          device={asked}
          ticket={ticket}
          reason={reasonText}
          recordChecked={dialogRecordChecked}
          onRecord={(checked) => setRecordOn((current) => ({ ...current, [asked.deviceId]: checked }))}
          onTicket={setTicket}
          onReason={setReasonText}
          onCancel={() => {
            setAsk(null);
            setTicket("");
            setReasonText("");
            restoreDialogOpener();
          }}
          onContinue={() => {
            if (ask.kind === "control") {
              if (asked.reasonRequired === true && cleanedReason(reasonText).length === 0) return;
              props.onStart(asked.deviceId, ask.accessMode, startFeatures(asked, dialogRecordChecked), reasonText);
            } else if (props.onStealth) {
              const justification = ticket.trim();
              if (justification.length === 0) return;
              if (asked.reasonRequired === true && cleanedReason(reasonText).length === 0) return;
              props.onStealth(asked.deviceId, justification, reasonText);
            }
            setAsk(null);
            setTicket("");
            setReasonText("");
          }}
        />
      ) : null}
      <div className={`device-explorer${treePanelCollapsed ? " device-explorer-tree-collapsed" : ""}`}>
        <nav
          id={treePanelId}
          className={treePanelCollapsed ? "device-tree device-tree-collapsed-rail" : "device-tree"}
          aria-label="Folders"
        >
          <div className="device-tree-panel-bar">
            <button
              type="button"
              className="device-tree-panel-toggle"
              aria-expanded={!treePanelCollapsed}
              aria-controls={treePanelId}
              aria-label={treePanelCollapsed ? "Show device folders" : "Hide device folders"}
              onClick={toggleTreePanel}
            >
              <span className="device-tree-panel-chevron" aria-hidden="true" />
            </button>
          </div>
          {treePanelCollapsed ? null : (
            <>
          {treeRow("root", "All devices", { kind: "root" }, counts.all, { key: ROOT_BRANCH, controls: `${treeId}-root` }, false)}
          <div id={`${treeId}-root`} className="device-tree-branch">
            {treeCatalog.map((organisation, index) => {
              if (!rootOpen && organisation.organisationId !== selectedOrg) return null;
              const departments = treeDepartments(organisation);
              const branch =
                departments.length > 0
                  ? { key: `org:${organisation.organisationId}`, controls: `${treeId}-org-${index}` }
                  : null;
              const open = branch ? !collapsed.has(branch.key) : true;
              return (
                <div key={organisation.organisationId} className="device-tree-group">
                  {treeRow(
                    organisation.organisationId,
                    organisation.name,
                    { kind: "organisation", organisationId: organisation.organisationId },
                    counts.organisations.get(organisation.organisationId) ?? 0,
                    branch,
                    false,
                  )}
                  {branch ? (
                    <div id={branch.controls} className="device-tree-branch">
                      {departments
                        .filter((department) => open || department.departmentId === selectedDept)
                        .map((department) => departmentRow(organisation.organisationId, department))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
            </>
          )}
        </nav>
        <div className="device-tree-list">
          <div className="device-table-wrap">
            <DataTable
              className="device-table"
              rows={sorted.slice(0, rowWindow)}
              rowKey={(device) => device.deviceId}
              columns={columns}
              sort={{ key: sort.column, direction: sort.direction }}
              onSort={onSort}
              tableId={DEVICES_TABLE_ID}
            />
          </div>
          {emptyText ? <p className="device-empty">{emptyText}</p> : null}
          {sorted.length > rowWindow ? (
            <button type="button" className="device-show-more" onClick={() => setRowWindow((size) => size + ROW_WINDOW)}>
              Show 200 more
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

type ReasonAsk =
  | { kind: "control"; deviceId: string; accessMode: "Attended" | "Unattended" }
  | { kind: "command"; deviceId: string };

function DeviceDetailsDialog(props: { device: DeviceRow; onClose: () => void }) {
  const dialog = useDialog();
  const titleId = useId();
  useDialogFocusTrap(dialog, props.onClose);
  const fields = deviceDetailFields(props.device);
  return (
    <dialog
      ref={dialog}
      className="grant-dialog"
      aria-labelledby={titleId}
      onClose={props.onClose}
      onCancel={(event) => {
        event.preventDefault();
        props.onClose();
      }}
    >
      <div className="fields device-details-dialog device-inventory-details">
        <h2 id={titleId}>{props.device.name}</h2>
        <dl className="device-details-list">
          {fields.map((field) => (
            <div key={field.key}>
              <dt>{field.label}</dt>
              <dd>{renderDeviceDetailValue(field)}</dd>
            </div>
          ))}
        </dl>
        <div className="actions">
          <button type="button" className="secondary" onClick={() => closeDialog(dialog.current)}>
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}

export { DEVICE_DETAIL_FIELD_KEYS, deviceDetailFields, dialogShowsSecretLikeLabel } from "./deviceDetailFields.tsx";

function reasonBlocked(device: DeviceRow, reason: string): boolean {
  return device.reasonRequired === true && cleanedReason(reason).length === 0;
}

function ReasonDialog(props: {
  ask: ReasonAsk;
  device: DeviceRow;
  ticket: string;
  reason: string;
  recordChecked: boolean;
  onRecord: (checked: boolean) => void;
  onTicket: (value: string) => void;
  onReason: (value: string) => void;
  onCancel: () => void;
  onContinue: () => void;
}) {
  const recordChoice = showsRecordChoice(props.device);
  const dialog = useDialog();
  const cancelRef = useRef(props.onCancel);
  cancelRef.current = props.onCancel;
  const title = props.ask.kind === "command" ? "Stealth Command" : operatorModeLabel(props.ask.accessMode);
  const blocked =
    reasonBlocked(props.device, props.reason) || (props.ask.kind === "command" && props.ticket.trim().length === 0);
  useDialogFocusTrap(dialog, () => cancelRef.current());
  return (
    <dialog
      ref={dialog}
      className="grant-dialog"
      aria-labelledby="session-reason-title"
      onCancel={(event) => {
        event.preventDefault();
        props.onCancel();
      }}
    >
      <form
        className="reason-dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (blocked) return;
          props.onContinue();
        }}
      >
        <h2 id="session-reason-title">{title}</h2>
        {props.ask.kind === "command" ? (
          <label>
            Justification
            <input
              aria-label="Stealth Command justification"
              value={props.ticket}
              maxLength={500}
              onChange={(event) => props.onTicket(event.target.value)}
            />
          </label>
        ) : null}
        {recordChoice ? (
          props.ask.kind === "command" ? (
            <div className="record-policy">
              <label className="record-choice" title="Record">
                Record this session
                <input
                  type="checkbox"
                  checked={stealthCommandRecordChecked(props.device)}
                  disabled
                  aria-label="Record this session"
                  readOnly
                />
              </label>
              <p className="record-policy-note">Recording follows the device&apos;s recording policy.</p>
            </div>
          ) : (
            <label className="record-choice" title="Record">
              Record this session
              <input
                type="checkbox"
                checked={props.device.recordingForced ? true : props.recordChecked}
                disabled={props.device.recordingForced === true}
                aria-label="Record this session"
                onChange={(event) => props.onRecord(event.target.checked)}
              />
            </label>
          )
        ) : null}
        <label>
          Reason
          <textarea
            aria-label="Session reason"
            rows={6}
            maxLength={1000}
            value={props.reason}
            onChange={(event) => props.onReason(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.stopPropagation();
            }}
          />
        </label>
        <div className="actions">
          <button type="button" className="secondary" onClick={props.onCancel}>
            Cancel
          </button>
          <button type="submit" disabled={blocked}>
            {props.ask.kind === "command" ? "Open Stealth Command" : "Continue"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

type PlacementDraft = {
  name: string;
  organisationName: string;
  departmentName: string;
  tags: { key: string; value: string }[];
};

export type PlacementCommit = {
  name: string;
  organisationName: string;
  departmentName: string;
  tags: { key: string; value: string }[];
};

export function DevicePlacement(props: {
  devices: DeviceRow[];
  catalog: DeviceFolder[];
  error: string | null;
  canEdit: boolean;
  canDelete?: boolean;
  onRename: (deviceId: string, name: string) => void;
  onPlace: (deviceId: string, organisationId: string, departmentId: string) => void;
  onCommit?: (deviceId: string, input: PlacementCommit) => void;
  onApprove: (deviceId: string) => void;
  onDelete?: (deviceId: string) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filters, setFilters] = useState<PlacementFilters>(NO_PLACEMENT_FILTERS);
  const [sort, setSort] = useState<PlacementSort>(DEFAULT_PLACEMENT_SORT);
  const facetMemo = useMemo(() => createPlacementFacetMemo(), []);
  const facets = facetMemo(props.devices, filters);
  const sorted = useMemo(() => sortPlacementRows(facets.rows, sort), [facets.rows, sort]);
  const knownTags = [...new Set(props.devices.flatMap((device) => device.tags.map(tagLabel)))].sort((left, right) =>
    left.localeCompare(right),
  );
  const editing = props.devices.find((device) => device.deviceId === editingId) ?? null;
  const deleting = props.devices.find((device) => device.deviceId === deleteId) ?? null;

  function save(device: DeviceRow, draft: PlacementDraft) {
    const input = {
      name: draft.name.trim(),
      organisationName: draft.organisationName.trim(),
      departmentName: draft.departmentName.trim(),
      tags: draft.tags,
    };
    if (props.onCommit) props.onCommit(device.deviceId, input);
    else if (input.name.length > 0 && input.name !== device.name) props.onRename(device.deviceId, input.name);
    setEditingId(null);
  }

  function toggle(column: PlacementFilterColumn, value: string) {
    setFilters(togglePlacementFilter(filters, column, value));
  }

  function onSort(key: string) {
    const column = PLACEMENT_FILTER_COLUMNS.find((item) => item === key);
    if (!column) return;
    setSort({ column, direction: sort.column === column && sort.direction === "asc" ? "desc" : "asc" });
  }

  function choices(column: PlacementFilterColumn): FilterChoice[] {
    const facet = facets.columns.get(column);
    const selected = filters.values.get(column);
    return (facet?.options ?? []).map((option) => ({
      key: `value:${option.value}`,
      label: option.label,
      count: String(option.count),
      checked: selected?.has(option.value) === true,
      onToggle: () => toggle(column, option.value),
    }));
  }

  const chips: FilterChip[] = [];
  for (const column of PLACEMENT_FILTER_COLUMNS) {
    for (const value of filters.values.get(column) ?? []) {
      chips.push({
        key: `value:${column}:${value}`,
        column: PLACEMENT_COLUMN_LABELS[column],
        label: value.length === 0 ? "(empty)" : value,
        onRemove: () => toggle(column, value),
      });
    }
  }

  function filterFor(column: PlacementFilterColumn) {
    return (
      <ColumnFilter label={PLACEMENT_COLUMN_LABELS[column]} choices={() => choices(column)} selected={selectedPlacementFilterCount(filters, column)} />
    );
  }

  const columns: DataColumn<DeviceRow>[] = [
    {
      key: "name",
      label: PLACEMENT_COLUMN_LABELS.name,
      defaultWidth: 120,
      minWidth: 96,
      sortable: true,
      rowHeader: true,
      filter: filterFor("name"),
      cell: (device) => device.name,
    },
    {
      key: "hostname",
      label: PLACEMENT_COLUMN_LABELS.hostname,
      defaultWidth: 136,
      minWidth: 96,
      sortable: true,
      filter: filterFor("hostname"),
      cell: (device) => (
        <span className="cell-clip" title={device.hostname}>
          {device.hostname}
        </span>
      ),
    },
    {
      key: "organisation",
      label: PLACEMENT_COLUMN_LABELS.organisation,
      defaultWidth: 128,
      minWidth: 96,
      sortable: true,
      filter: filterFor("organisation"),
      cell: (device) => device.organisationName ?? "",
    },
    {
      key: "department",
      label: PLACEMENT_COLUMN_LABELS.department,
      defaultWidth: 128,
      minWidth: 96,
      sortable: true,
      filter: filterFor("department"),
      cell: (device) => device.departmentName ?? "",
    },
    {
      key: "tags",
      label: PLACEMENT_COLUMN_LABELS.tags,
      defaultWidth: 128,
      minWidth: 72,
      sortable: true,
      filter: filterFor("tags"),
      cell: (device) => tags(device),
    },
    {
      key: "status",
      label: PLACEMENT_COLUMN_LABELS.status,
      defaultWidth: 104,
      minWidth: 72,
      sortable: true,
      filter: filterFor("status"),
      cell: (device) => device.status,
    },
    {
      key: "actions",
      label: "Actions",
      defaultWidth: 148,
      minWidth: 120,
      className: "device-actions",
      cell: (device) => (
        <div className="actions">
          {props.canEdit ? <IconAction label="Edit" kind="edit" onClick={() => setEditingId(device.deviceId)} /> : null}
          {props.canDelete && props.onDelete ? (
            <IconAction label="Delete" kind="delete" onClick={() => setDeleteId(device.deviceId)} />
          ) : null}
          {props.canEdit && device.status === "Pending" ? (
            <IconAction label="Approve" kind="approve" onClick={() => props.onApprove(device.deviceId)} />
          ) : null}
          {device.status === "Pending" && device.osVersion ? <span>{device.osVersion}</span> : null}
          {device.status === "Pending" && osClaimMismatch(device.platform, device.osVersion) ? (
            <span>Claimed platform does not match the inventory OS</span>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <section className="panel">
      <header className="panel-head">
        <div>
          <h2>Devices</h2>
          <p>Choose Edit to change a device. A new organisation, department, or tag is created when you save.</p>
        </div>
      </header>
      {props.error ? <p className="banner error">{props.error}</p> : null}
      <FilterChips chips={chips} onClearAll={() => setFilters(NO_PLACEMENT_FILTERS)} />
      {props.devices.length > 0 && sorted.length === 0 && hasPlacementFilters(filters) ? (
        <p>No devices match these column filters.</p>
      ) : null}
      <div className="device-table-wrap">
        <DataTable
          className="device-table placement-table"
          rows={sorted}
          rowKey={(device) => device.deviceId}
          columns={columns}
          sort={{ key: sort.column, direction: sort.direction }}
          onSort={onSort}
          tableId={SETTINGS_DEVICES_TABLE_ID}
        />
      </div>
      {editing ? (
        <DeviceEditDialog
          device={editing}
          catalog={props.catalog}
          knownTags={knownTags}
          onClose={() => setEditingId(null)}
          onSave={(draft) => save(editing, draft)}
        />
      ) : null}
      {deleting && props.onDelete ? (
        <DeviceDeleteDialog
          device={deleting}
          onClose={() => setDeleteId(null)}
          onConfirm={() => {
            props.onDelete?.(deleting.deviceId);
            setDeleteId(null);
          }}
        />
      ) : null}
    </section>
  );
}

function DeviceDeleteDialog(props: { device: DeviceRow; onClose: () => void; onConfirm: () => void }) {
  const dialog = useDialog();
  const titleId = useId();
  useDialogFocusTrap(dialog, props.onClose);
  return (
    <dialog
      ref={dialog}
      className="grant-dialog"
      aria-labelledby={titleId}
      onClose={props.onClose}
      onCancel={(event) => {
        event.preventDefault();
        props.onClose();
      }}
    >
      <form
        className="fields"
        onSubmit={(event) => {
          event.preventDefault();
          props.onConfirm();
        }}
      >
        <h2 id={titleId}>Delete {props.device.name}?</h2>
        <p>This removes the device from inventory. Live sessions for it are ended by the server.</p>
        <div className="actions">
          <button type="button" className="secondary" onClick={() => closeDialog(dialog.current)}>
            Cancel
          </button>
          <button type="submit">Delete device</button>
        </div>
      </form>
    </dialog>
  );
}

function DeviceEditDialog(props: {
  device: DeviceRow;
  catalog: DeviceFolder[];
  knownTags: string[];
  onClose: () => void;
  onSave: (draft: PlacementDraft) => void;
}) {
  const dialog = useDialog();
  const [name, setName] = useState(props.device.name);
  const [organisationName, setOrganisationName] = useState(props.device.organisationName ?? "");
  const [departmentName, setDepartmentName] = useState(props.device.departmentName ?? "");
  const [deviceTags, setDeviceTags] = useState(props.device.tags.map((tag) => ({ ...tag })));
  const organisation = props.catalog.find((item) => item.name.trim().toLowerCase() === organisationName.trim().toLowerCase());
  const departments = organisation?.departments.map((item) => item.name) ?? [];
  return (
    <dialog ref={dialog} className="grant-dialog" aria-labelledby="edit-device-title" onClose={props.onClose}>
      <form
        className="fields"
        onSubmit={(event) => {
          event.preventDefault();
          props.onSave({ name, organisationName, departmentName, tags: deviceTags });
        }}
      >
        <h2 id="edit-device-title">Edit device</h2>
        <label>
          Name
          <input aria-label={`Name for ${props.device.name}`} value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label>
          Organisation
          <NameCombo
            label={`Organisation for ${props.device.name}`}
            value={organisationName}
            options={props.catalog.map((item) => item.name)}
            disabled={false}
            onChange={setOrganisationName}
          />
        </label>
        <label>
          Department
          <NameCombo
            label={`Department for ${props.device.name}`}
            value={departmentName}
            options={departments}
            disabled={false}
            onChange={setDepartmentName}
          />
        </label>
        <TagEditor deviceName={props.device.name} tags={deviceTags} choices={props.knownTags} disabled={false} onChange={setDeviceTags} />
        <div className="actions">
          <button type="button" onClick={() => closeDialog(dialog.current)}>
            Cancel
          </button>
          <button type="submit">Save</button>
        </div>
      </form>
    </dialog>
  );
}

function NameCombo(props: {
  label: string;
  value: string;
  options: string[];
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const listId = `combo-${props.label.replace(/[^a-z0-9]+/gi, "-")}`;
  return (
    <>
      <input
        aria-label={props.label}
        list={listId}
        value={props.value}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.value)}
      />
      <datalist id={listId}>
        {props.options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </>
  );
}

